import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { MultiDropdownOption, MultiDropdownProps } from "./types";
import { classNames } from "helpers";
import { GetDropdownItem } from "./consts";

import { Tooltip } from "ui/components/Tooltip";
import MultiDropdownItem from "./MultiDropdownItem";

const MENU_MAX_HEIGHT = 224; // max-h-56
const MENU_OFFSET = 4;

const MultiDropdown: React.FC<MultiDropdownProps> = (
  props: MultiDropdownProps
) => {
  const {
    name,
    required,
    disabled,
    label,
    error,
    value,
    direction,
    options,
    placeholder,
    width,
    height = "normal",
    onChange,
    cssClassName,
  } = props;

  // Promena: Držanje više odabranih stavki u nizu
  const [selectedItems, setSelectedItems] = useState<MultiDropdownOption[]>([]);

  const [open, setOpen] = useState(false);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  // Popis se renderira u portalu jer ga scroll kontejneri (npr. ModalBody s overflow-y-auto)
  // inače režu; pozicija se računa iz okvira trigger elementa. Isto kao u `Dropdown`.
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});

  const updateMenuPosition = useCallback(() => {
    if (!wrapperRef.current) return;

    const rect = wrapperRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUp =
      direction === "up" || (spaceBelow < MENU_MAX_HEIGHT && rect.top > spaceBelow);

    setMenuStyle({
      position: "fixed",
      left: rect.left,
      width: rect.width,
      ...(openUp
        ? { bottom: window.innerHeight - rect.top + MENU_OFFSET }
        : { top: rect.bottom + MENU_OFFSET }),
    });
  }, [direction]);

  useEffect(() => {
    if (!open) return;

    updateMenuPosition();

    // Fixed pozicija ne prati scroll pretka — capture faza hvata scroll bilo kojeg kontejnera.
    window.addEventListener("scroll", updateMenuPosition, true);
    window.addEventListener("resize", updateMenuPosition);

    return () => {
      window.removeEventListener("scroll", updateMenuPosition, true);
      window.removeEventListener("resize", updateMenuPosition);
    };
  }, [open, updateMenuPosition]);

  const isError = !!error && (!value || value.length === 0);

  const handleOpen = () => setOpen(!open);

  // Popis je u portalu (izvan wrappera) — bez ove provjere bi se zatvorio na mousedown
  // prije nego klik na stavku uopće okine.
  const handleClickOutside = useCallback((event: MouseEvent) => {
    const target = event.target as Node;

    if (wrapperRef.current?.contains(target)) return;
    if (listRef.current?.contains(target)) return;

    setOpen(false);
  }, []);

  useEffect(() => {
    if (!open) return;

    document.addEventListener("mousedown", handleClickOutside);

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open, handleClickOutside]);

  useEffect(() => {

    if (value && value.length > 0) {
      const selected = options.filter((option) => value.includes(option.value));
      setSelectedItems(selected);
    } else {
      // Ako nema inicijalne vrijednosti, može se prikazati placeholder ili prazan niz
      setSelectedItems([]);
    }
  }, [value, options]);

  const handleClicked = (event: React.MouseEvent<HTMLElement>, clickedValue: string) => {
    event.preventDefault();

    const item = GetDropdownItem(options, clickedValue, placeholder);
    const isSelected = selectedItems.some(
      (selected) => selected.value === item.value
    );

    let updatedSelectedItems;
    if (isSelected) {
      updatedSelectedItems = selectedItems.filter(
        (selected) => selected.value !== item.value
      );
    } else {
      updatedSelectedItems = [...selectedItems, item];
    }

    setSelectedItems(updatedSelectedItems);
    onChange(name, updatedSelectedItems);
    setOpen(false);
  };

  const widthClassName = () => {
    switch (width) {
      case "min":
        return "w-24";
      case "small":
        return "w-48";
      case "medium":
        return "w-96";
      case "large":
        return "w-full";
      case "full":
        return "w-full";
      default:
        return "w-full";
    }
  };

  const heightClassName = () => {
    switch (height) {
      case "small":
        return "py-1.5";
      case "normal":
      default:
        return "py-2.5";
    }
  };

  const requiredClassName = () => {
    let result = "";
    if (isError && required) {
      result =
        "!bg-red-50 !border-red-500 !text-red-900 !placeholder-red-700 text-sm  focus:ring-red-500 focus:border-red-500 block w-full p-2.5";
    } else {
      result = "";
    }

    return result;
  };

  const commonClassName = () => {
    if (cssClassName) return cssClassName;

    return classNames("relative cursor-default rounded-sm pl-3 pr-3 text-left border border-slate-300 bg-slate-50 text-[#344054] focus:outline-none focus:ring-0.5 focus:ring-blue-500 sm:text-sm sm:leading-6 disabled:cursor-default disabled:bg-gray-100 disabled:text-gray-900 disabled:border-gray-300 disabled:shadow-none", heightClassName());
  };

  // Funkcija za prikaz odabranih stavki
  const renderSelectedItems = () => {
    if (selectedItems.length === 0) {
      return (
        <span className="block truncate text-gray-500">{placeholder || "Odaberite..."}</span>
      );
    }

    return (
      <div className="flex flex-wrap gap-1">
        {selectedItems.map((item) => (
          <span
            key={item.value}
            className="bg-blue-100 text-blue-600 rounded-sm px-2 py-1 text-xs font-medium flex items-center"
          >
            {item.text}
            <button
              className="ml-1 text-blue-600 hover:text-blue-800"
              onClick={(e) => {
                e.stopPropagation();
                handleClicked(e, item.value);
              }}
            >
              ✕
            </button>
          </span>
        ))}
      </div>
    );
  };

  return (
    <>
      {label && (
        <label
          id="listbox-label"
          className="block text-sm mb-2 font-medium leading-6 text-[#344054]"
        >
          {label}
          {required && <span className="text-red-500"> *</span>}
        </label>
      )}
  
      <div ref={wrapperRef} className={classNames(widthClassName(), "relative min-w-full")}>
        <button
          type="button"
          className={classNames(
            commonClassName(),
            widthClassName(),
            requiredClassName(),
            "min-h-[46px] ring-inset ring-gray-300  focus:ring-blue-500 sm:text-sm sm:leading-6"
          )}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-labelledby="listbox-label"
          onClick={handleOpen}
          disabled={disabled}
        >
          <span className="flex items-center">{renderSelectedItems()}</span>
          <span className="pointer-events-none absolute inset-y-0 right-0 ml-3 flex items-center pr-2">
            <svg
              className="h-5 w-5 text-gray-500"
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M10 3a.75.75 0 01.55.24l3.25 3.5a.75.75 0 11-1.1 1.02L10 4.852 7.3 7.76a.75.75 0 01-1.1-1.02l3.25-3.5A.75.75 0 0110 3zm-3.76 9.2a.75.75 0 011.06.04l2.7 2.908 2.7-2.908a.75.75 0 111.1 1.02l-3.25 3.5a.75.75 0 01-1.1 0l-3.25-3.5a.75.75 0 01.04-1.06z"
                clipRule="evenodd"
              />
            </svg>
          </span>
        </button>
        {open &&
          createPortal(
            <ul
              ref={listRef}
              style={menuStyle}
              className="z-[60] max-h-56 overflow-auto rounded-sm bg-white text-base shadow-2xl ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm"
              tabIndex={-1}
              role="listbox"
              aria-labelledby="listbox-label"
            >
              {options.map((item: MultiDropdownOption, index: number) => (
                <MultiDropdownItem
                  key={index}
                  text={item.text}
                  value={item.value}
                  image={item.image}
                  onClick={handleClicked}
                  isSelected={selectedItems.some(
                    (selected) => selected.value === item.value
                  )}
                />
              ))}
            </ul>,
            document.body
          )}
        {isError && (
          <div className="absolute right-2 top-2/4 -translate-y-1/2 ">
            <Tooltip description={error} />
          </div>
        )}
      </div>
    </>
  );
};

export default React.memo(MultiDropdown);
